"use client";

import { useEffect, useRef } from "react";

export default function MicrophoneWaveform({
  stream,
  title = "",
  subtitle = "",
  ariaLabel = "",
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!stream || !canvasRef.current) return undefined;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return undefined;

    let animationFrame = null;
    let audioContext = null;
    let source = null;
    let analyser = null;

    try {
      audioContext = new AudioContextClass();
      audioContext.resume().catch(() => {});
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.75;
      source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
    } catch {
      return undefined;
    }

    const frequencyData = new Uint8Array(analyser.frequencyBinCount);
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    const draw = () => {
      if (!context || !canvas) return;
      const ratio = window.devicePixelRatio || 1;
      const displayWidth = Math.max(280, canvas.clientWidth);
      const displayHeight = Math.max(64, canvas.clientHeight);
      const width = Math.round(displayWidth * ratio);
      const height = Math.round(displayHeight * ratio);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      analyser.getByteFrequencyData(frequencyData);
      context.clearRect(0, 0, width, height);
      const barCount = 36;
      const gap = 3 * ratio;
      const barWidth = Math.max(
        2 * ratio,
        (width - gap * (barCount - 1)) / barCount,
      );
      const center = height / 2;

      for (let index = 0; index < barCount; index += 1) {
        const dataIndex = Math.floor((index / barCount) * frequencyData.length);
        const strength = frequencyData[dataIndex] / 255;
        const barHeight = Math.max(4 * ratio, strength * height * 0.88);
        const x = index * (barWidth + gap);
        const y = center - barHeight / 2;
        context.fillStyle =
          strength > 0.55 ? "#ef4444" : strength > 0.2 ? "#6366f1" : "#a5b4fc";
        if (typeof context.roundRect === "function") {
          context.beginPath();
          context.roundRect(x, y, barWidth, barHeight, barWidth / 2);
          context.fill();
        } else {
          context.fillRect(x, y, barWidth, barHeight);
        }
      }
      animationFrame = window.requestAnimationFrame(draw);
    };

    draw();
    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      try {
        source?.disconnect();
        analyser?.disconnect();
        audioContext?.close();
      } catch {
        // The browser may already have released the audio graph.
      }
    };
  }, [stream]);

  return (
    <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-red-600">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          {title}
        </span>
        <span className="text-xs text-slate-500">{subtitle}</span>
      </div>
      <canvas
        ref={canvasRef}
        className="h-16 w-full"
        aria-label={ariaLabel}
      />
    </div>
  );
}
